import { X } from 'lucide-react'
import { type ReactNode, type RefObject, useCallback, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../cn'
import { type CloseReason, useModalOverlay } from '../overlay'

export interface DrawerProps {
  open: boolean
  /** Single close path: X, Escape and backdrop all call it. */
  onClose: (reason: CloseReason) => void
  /** Accessible title; shown in the header unless `hideTitle`. */
  title: ReactNode
  hideTitle?: boolean
  side?: 'left' | 'right'
  children?: ReactNode
  footer?: ReactNode
  initialFocusRef?: RefObject<HTMLElement | null>
  className?: string
}

/** Edge-anchored modal panel (mobile navigation, record side panels). */
export function Drawer({
  open,
  onClose,
  title,
  hideTitle,
  side = 'right',
  children,
  footer,
  initialFocusRef,
  className,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const hostRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const onEscape = useCallback(() => onClose('escape'), [onClose])

  useModalOverlay({ open, panelRef, hostRef, onEscape, initialFocusRef })

  if (!open) return null

  return createPortal(
    <div ref={hostRef} className="fixed inset-0 z-[60]">
      <div
        aria-hidden="true"
        data-testid="drawer-backdrop"
        className="absolute inset-0 animate-fade-in bg-[#17312e]/40"
        onClick={() => onClose('backdrop')}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'absolute inset-y-0 flex w-[min(22rem,88vw)] flex-col bg-surface shadow-pop focus:outline-none',
          side === 'left' ? 'left-0 animate-slide-in-left' : 'right-0 animate-slide-in-right',
          className,
        )}
      >
        <header
          className={cn(
            'flex items-center justify-between gap-3 border-b border-line px-5 py-3',
            hideTitle && 'border-b-0',
          )}
        >
          <h2
            id={titleId}
            className={cn('font-display text-lg font-semibold text-ink', hideTitle && 'sr-only')}
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={() => onClose('close-button')}
            aria-label="Close panel"
            className="ml-auto inline-flex size-9 items-center justify-center rounded-md text-sage hover:bg-wash hover:text-ink"
          >
            <X aria-hidden="true" className="size-5" />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        {footer ? <footer className="border-t border-line px-5 py-4">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  )
}
